import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import { LoaderTalos, LoaderWrapper } from '../../LoaderTalos';
import { ENTITY_INTERNAL_ROW_ID } from '../EntityAdminClass';
import type { BaseEntityAdminProps, EntityAdminRecord } from '../types';
import { hasAnyAddAllowed } from '../utils';
import { CustomDrawer } from './CustomDrawer/CustomDrawer';
import { EntityAdminDrawerProvider } from './EntityAdminDrawerContext';
import {
  InputsAndDropdownsDrawer,
  type BaseEntityAdminDrawerProps,
} from './InputsAndDropdownsDrawer/InputsAndDropdownsDrawer';
import { ModifyJsonDrawer } from './ModifyJsonDrawer';

interface EntityAdminDrawerProps<TRecord extends EntityAdminRecord, TDrawerRecord extends EntityAdminRecord>
  extends
    BaseEntityAdminDrawerProps<TRecord, TDrawerRecord>,
    Pick<
      BaseEntityAdminProps<TRecord, TDrawerRecord>,
      'customDrawerLayout' | 'drawerType' | 'allowAddEntity' | 'allowEditEntity' | 'allowDeleteEntity'
    > {
  addingChildEntity: boolean;
  /** Default values function to use when creating a new entity. */
  newEntityDefault?: () => Partial<TDrawerRecord>;
}

export const EntityAdminDrawer = <TRecord extends EntityAdminRecord, TDrawerRecord extends EntityAdminRecord>({
  drawerOptions,
  drawerType = 'InputsAndDropdowns',
  ...props
}: EntityAdminDrawerProps<TRecord, TDrawerRecord>) => {
  const keyId = useMemo(() => {
    // get key for entity being shown so we can properly re-render the drawer when the context changes
    if (props.selectedEntities.length === 0) {
      return 'new-' + uuid();
    }
    // if the context changes about the selection, we need to re-render the drawer
    return props.selectedEntities.map(entity => entity[ENTITY_INTERNAL_ROW_ID]).join('-');
  }, [props.selectedEntities]);

  const isEditing = useMemo(
    () => props.selectedEntities?.length > 0 && !props.addingChildEntity,
    [props.selectedEntities, props.addingChildEntity]
  );

  if (!hasAnyAddAllowed(props.allowAddEntity) && !props.allowEditEntity) {
    // No permissions to add or edit entities using the drawer
    return null;
  }

  if (drawerType !== 'JSON' && drawerOptions.length === 0) {
    // For non-JSON drawers, show a loading state if drawerOptions is empty
    return (
      <EntityAdminDrawerProvider>
        <LoaderWrapper background="backgroundContent">
          <LoaderTalos />
        </LoaderWrapper>
      </EntityAdminDrawerProvider>
    );
  }

  return (
    <EntityAdminDrawerProvider>
      {drawerType === 'JSON' && <ModifyJsonDrawer<TRecord, TDrawerRecord> key={keyId} {...props} />}
      {drawerType === 'InputsAndDropdowns' && drawerOptions != null && (
        <InputsAndDropdownsDrawer<TRecord, TDrawerRecord>
          key={keyId}
          drawerOptions={drawerOptions}
          isEditing={isEditing}
          {...props}
        />
      )}
      {drawerType === 'custom' && drawerOptions != null && props.customDrawerLayout != null && (
        <CustomDrawer<TRecord, TDrawerRecord>
          key={keyId}
          drawerOptions={drawerOptions}
          isEditing={isEditing}
          customDrawerLayout={props.customDrawerLayout}
          {...props}
        />
      )}
    </EntityAdminDrawerProvider>
  );
};
